import { FeatureCollection } from 'geojson';
import { geojsonToGpx, GeojsonToGpxOptions, GpxWaypoint } from '../../utils/geojsonToGpx';

/** The parts of a GraphQL HikeHighlight the export reads; coordinates are [lon, lat]. */
export interface HikeHighlightLike {
  title?: string | null;
  description?: string | null;
  type?: string | null;
  coordinates?: number[] | null;
}

/** A hike's highlights as GPX waypoints, skipping any without a usable position. */
export const highlightsToGpxWaypoints = (highlights: HikeHighlightLike[] | null | undefined = []): GpxWaypoint[] =>
  (highlights ?? []).flatMap(highlight => {
    const [lon, lat] = highlight?.coordinates ?? [];
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return [];
    return [{
      lat,
      lon,
      name: highlight.title?.trim() || null,
      desc: highlight.description?.trim() || null,
      type: highlight.type || null,
    }];
  });

/**
 * The GPX for a hike download. Both variants are tracks, never routes: Garmin
 * handhelds keep only ~250 points per <rte> (a 770-point, 25 km hike loaded as
 * ~10 km and "incomplete" on a GPSMAP 66i) and may re-route between them, while a
 * <trk> loads in full, as drawn, almost everywhere. The hike's highlights go in
 * as <wpt>s.
 *
 * - garmin: every leg merged into one <trk> with one <trkseg>.
 * - standard: one <trk> per hike day, named like the planner's days.
 */
export const hikeFeaturesToGpx = (
  featureCollection: FeatureCollection,
  metadata: Pick<GeojsonToGpxOptions, 'creator' | 'metadata'>,
  trackName: string,
  { garmin = false, highlights = [] }: { garmin?: boolean; highlights?: HikeHighlightLike[] | null } = {}
): string =>
  geojsonToGpx(featureCollection, null, {
    ...metadata,
    outputType: 'trk',
    trackName,
    waypoints: highlightsToGpxWaypoints(highlights),
    ...(garmin ? { singleTrack: true } : { mergeRoutesByProperty: 'hikeDayNumber' }),
  });
